// src/data/editions/sport.js
//
// SPORT (GENERIC) edition — the standard skin for any sport org (clubs,
// federations, academies) that hasn't been given a specialized vertical.
// A sport org doesn't think in claims; it thinks in ATHLETE AVAILABILITY:
//   load -> fatigue -> missed sessions -> games missed -> extended injury.
// DRAFT copy — reviewed via docs/plans/EDITIONS-REVIEW.md.

const sport = {
    id: 'sport',
    label: 'Sport — Generic',

    palette: { deep: '#0c4a6e', blue: '#0284c7', yellow: '#facc15' },

    pageTitles: {
        'cc-overview': 'Team Readiness Overview',
        'cc-triangulation': 'Triangulation: Athletes, the Organization, the Average',
        'cc-data': 'Data Intake: The Organization’s Own Figures',
        'cc-ai': 'AI Intelligence & Charting',
        'cc-readiness': 'Readiness & Strain',
        'cc-predictive': 'Availability Forecast',
        'cc-roi': 'Program ROI & Availability',
        'cc-feedback': 'Anonymous Pulse & Sentiment',
        default: 'Team Readiness Command Center'
    },

    trendSignalLabel: 'Availability signal',

    summary: {
        source: 'demo', // drives the console's "Modeled example" badge (T0.3)
        capacityScore: 82,
        capacityTrend: 3,
        workforce: 120,
        highSignalCohort: 18,
        topPriorityGroup: 'Senior Team',
        topPulseTheme: 'Schedule & travel load',
        participationRate: 71,
        modeledClaimsAvoidance: '$310K',
        modeledStdClaims: { low: 6, high: 10, windowDays: 30 },
        modeledLtdTransition: { low: 1, high: 2 },
        leaveProbability: { mid: 18, low: 14, high: 22 },
        // Overview roll-up card copy (2nd + 3rd cards).
        outlookTitle: 'Availability Outlook',
        outlookNoun: 'missed games / sessions',
        outlookUnit: 'events',
        financialNoun: 'program value'
    },

    capacityTrend: [
        { date: '1st', capacity: 79, signal: 22 },
        { date: '4th', capacity: 80, signal: 21 },
        { date: '8th', capacity: 78, signal: 24 },
        { date: '12th', capacity: 81, signal: 20 },
        { date: '16th', capacity: 80, signal: 21 },
        { date: '20th', capacity: 82, signal: 18 },
        { date: '24th', capacity: 81, signal: 19 },
        { date: '28th', capacity: 82, signal: 18 },
        { date: 'Today', capacity: 82, signal: 18 }
    ],

    readiness: {
        scoreLabel: 'Team Readiness Score',
        signalSubtext: 'training + competition load',
        groups: [
            { name: 'Senior Team', pressure: 79, note: 'Competition + travel load', responses: 26 },
            { name: 'U18 High Performance', pressure: 74, note: 'Exam season + training volume', responses: 21 },
            { name: 'Development Squad', pressure: 70, note: 'Rising training load', responses: 18 },
            { name: 'Coaching Staff', pressure: 66, note: 'Within normal range', responses: 9 },
            { name: 'Support Staff', pressure: 62, note: 'Within normal range', responses: 4 } // below floor
        ],
        signals: [
            { label: 'Self-reported fatigue trend', value: '+5%', tier: 'Intermediate', tone: 'amber' },
            { label: 'Missed / modified sessions', value: '+7%', tier: 'Lagging', tone: 'amber' },
            { label: 'Soreness & niggle reports', value: '+4%', tier: 'Lagging', tone: 'amber' },
            { label: 'Coach check-in signal', value: 'Steady', tier: 'Intermediate', tone: 'emerald' }
        ]
    },

    feedback: {
        scopes: [
            { id: 'org', label: 'Whole club', responses: 74 },
            { id: 'seniorteam', label: 'Senior Team', responses: 26 },
            { id: 'u18', label: 'U18 High Performance', responses: 21 },
            { id: 'development', label: 'Development Squad', responses: 18 },
            { id: 'supportstaff', label: 'Support Staff', responses: 4 } // below floor -> suppressed
        ],
        submissions: [
            { id: 1, time: '2 hours ago', theme: 'Fatigue', sentiment: 'risk', scope: 'seniorteam',
              text: 'Three games in four nights plus the bus travel - the recovery windows are just gone. Legs are dead by the weekend.' },
            { id: 2, time: '5 hours ago', theme: 'Tools', sentiment: 'positive', scope: 'u18',
              text: 'The weekly check-in got my load flagged before I said anything. Coach adjusted my minutes and it honestly helped.' },
            { id: 3, time: 'Yesterday', theme: 'Culture', sentiment: 'positive', scope: 'development',
              text: 'The plan for call-ups is way clearer this season. Knowing where I stand takes a lot of the pressure off.' },
            { id: 4, time: 'Yesterday', theme: 'Workload', sentiment: 'risk', scope: 'seniorteam',
              text: 'Video, gym, practice and school visits in the same day is a lot. Something has to give.' },
            { id: 5, time: '2 days ago', theme: 'Recognition', sentiment: 'positive', scope: 'u18',
              text: 'My coach noticed I was flat and asked how I was doing before drills started. That mattered.' }
        ],
        sentiment: {
            positiveSummary: 'Strong response to load management acting on check-in data and clearer selection communication.',
            emergingTheme: 'Compressed game schedules and travel are the most frequent sources of fatigue.'
        }
    },

    predictive: {
        title: 'The Availability Funnel',
        intro: 'Sustained load in the high-load athlete cohort is a leading driver of missed and modified sessions. Left unmanaged, those surface as games missed and, in the tail, extended injury absences. This is directional from leading signals - not a forecast - and needs a full season window and a comparison baseline before any causal claim.',
        lever: 'Earlier load management',
        ownLabel: 'Missed-session rate',
        tiers: [
            { value: '120', label: 'Full roster & staff', source: 'observed', tone: 'neutral', width: 100 },
            { value: '18', label: 'High-load athlete cohort', source: 'observed', tone: 'brand', width: 88 },
            { value: '6-10', label: 'Modeled missed / modified sessions - month', source: 'modeled', tone: 'amber', width: 72 },
            { value: '2-4', label: 'Modeled games missed or modified', source: 'modeled', tone: 'amber', width: 58 },
            { value: '1-2', label: 'Extended injury absences - season tail', source: 'modeled', tone: 'muted', width: 44 }
        ],
        cards: [
            { title: 'Load-Absence Association', value: '~4.2x', valueTone: 'amber', level: 'Intermediate', source: 'modeled', dark: false,
              subtext: 'Modeled association - not causal',
              body: 'Squads sustaining low readiness and high load show a higher rate of missed and modified sessions. In comparable modeled groups the association is roughly 4.2x - directional, and confirmed only against the club’s own baseline.' },
            { title: 'Modeled Availability Risk', value: '18%', valueTone: 'white', level: 'Lagging', source: 'modeled', dark: true,
              subtext: 'Midpoint - 14-22% band',
              body: 'Modeled probability that sustained load in a high-load squad costs an athlete a game block this month. The band widens or narrows as the club’s baseline data is added.' },
            { title: 'Proactive Support Lift', value: '+34%', valueTone: 'emerald', level: 'Leading', source: 'observed', dark: false,
              subtext: 'Observed in engaged cohorts - attribution pending',
              body: 'Athletes engaged with AI Coach recommendations route earlier into recovery, physio and support resources - a leading indicator, shown as an association rather than a causal result.' }
        ],
        footer: {
            lead: 'These are models, not forecasts.',
            note: 'Missed and modified sessions are the leading, attributable metrics EAS owns. Games missed and extended injury absences are lagging outcomes that require a season-long window and a comparison baseline before any causal claim. EAS reports what it measures and labels what it models.'
        }
    },

    roi: {
        currency: 'CAD',
        locale: 'en-CA',
        benchSource: 'sector reference',
        replacementMultiple: 0.4,
        turnoverRate: 10,
        termLabels: {
            lostTime: 'Protected Availability',
            lostTimeBasis: 'Avoided athlete-absence days x program day value x modeled reduction.'
        }
    },

    copilot: { exampleScope: 'Senior Team' }
};

export default sport;
